import ReactDOM from 'react-dom/client';
import html2canvas from 'html2canvas';
import {
  ShipmentDetails,
  ShipmentLabelTypeEnum,
  ShippingPickupTypeEnum,
  TimelineShippingLabelAction,
} from '@interfaces/models/timeline';
import { Product } from '@interfaces/models/orderSaleProduct';
import Icon from '@components/atoms/icon/icon';
import UrlActionView from '@components/orders-sales/product-timeline/step-info/url-action';
import clsx from 'clsx';
import { useCopyToClipboard } from 'usehooks-ts';
import sanitizer from '@helpers/sanitizer';
import { useTranslation } from 'next-i18next';
import { useState, use, useMemo, useRef, useEffect } from 'react';
import { DropOff } from '@components/orders-sales/popin/get-shipping-label/drop-off';
import useGetShippingAddresses from '@hooks/orders-sales/use-get-shipping-address';
import AddressCard from '@components/orders-sales/popin/get-shipping-label/address/address-card';
import { useRouter } from 'next/router';
import { TimelineContext } from '@context/timeline.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { StepInfoContext } from '@context/step-info.context';
import { getOrderSaleURLQueries, onImageError, productImage, updateURLQueries } from '../../../helper';
import styles from './shipping.module.scss';
import ShippingInstructionDescription from './shipping-instruction';

const langPrefix = 'MY_ACCOUNT.ORDERS_SALES.ACTION.GET_SHIPPING_LABEL.';
const classNamePath = 'shippingInfo';

type DownloadableElements = {
  root: ReactDOM.Root;
  view: HTMLDivElement;
};
function appendElementToBody(element: React.ReactElement): DownloadableElements {
  const view = document.createElement('div');
  view.id = `downloadable-view-${Date.now()}`;
  view.style.position = 'fixed';
  view.style.top = '0';
  view.style.left = '-9999px';
  const root = ReactDOM.createRoot(view);
  root.render(element);
  document.body.appendChild(view);

  return { root, view };
}

function downloadAndCleanUp({ root, view }: DownloadableElements, fileName: string, urlData: string): void {
  const link = document.createElement('a');
  link.href = urlData;
  link.download = fileName;
  link.click();
  root.unmount();
  view.remove();
}

async function getQrUrlData(url: string): Promise<string> {
  const response = await fetch(url);
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(`${reader.result}`);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function renderAndDownload(element: React.ReactElement, fileName: string, qrCodeUrl: string): Promise<void> {
  const downloadableElements = appendElementToBody(element);

  const recover = () =>
    getQrUrlData(qrCodeUrl).then((data) => downloadAndCleanUp(downloadableElements, fileName, data));

  const node = document.getElementById(downloadableElements.view.id);

  if (node == null) {
    return recover();
  }
  return new Promise(requestAnimationFrame)
    .then(() => html2canvas(node, { logging: false, useCORS: true }))
    .then((canvas) => canvas.toDataURL('image/jpeg', 1))
    .then((dataUrl) => downloadAndCleanUp(downloadableElements, fileName, dataUrl))
    .catch(() => recover());
}

const DownloadView: React.FC<{
  shipmentDetails: ShipmentDetails;
  product: Product;
  shipByLabel: string;
  trackingLabel: string;
  refLabel: string;
}> = ({ shipmentDetails, product, shipByLabel, trackingLabel, refLabel }) => {
  const { chosenCarrier, shipByDate, labelUrl, trackingNumber } = shipmentDetails;
  const { carrierName, iconUrl } = chosenCarrier || {};

  return (
    <div className={styles[`${classNamePath}__downloadCodeView`]}>
      <div className={styles[`${classNamePath}__downloadCodeView--inner`]}>
        <div className={styles[`${classNamePath}__downloadCodeView__product`]}>
          <img
            className={styles[`${classNamePath}__downloadCodeView__product--image`]}
            src={productImage(product.imageUrl)}
            alt={product.productName}
            onError={onImageError}
          />
          <div className={styles[`${classNamePath}__downloadCodeView__product--info`]}>
            <div className={styles[`${classNamePath}__downloadCodeView__product--info--name`]}>{product.brandName}</div>
            <div className={styles[`${classNamePath}__downloadCodeView__product--info--type`]}>
              {product.productName}
            </div>
            <div className={styles[`${classNamePath}__downloadCodeView__product--info--ref`]}>
              {refLabel}. {product.productId}
            </div>
          </div>
        </div>
        <div className={styles[`${classNamePath}__downloadCodeView__shipdate`]}>
          <div className={styles[`${classNamePath}__downloadCodeView__shipdate--label`]}>{shipByLabel}</div>
          <div className={styles[`${classNamePath}__downloadCodeView__shipdate--value`]}>{shipByDate}</div>
        </div>
        <div className={styles[`${classNamePath}__downloadCodeView__shipdate`]}>
          <img
            alt={carrierName}
            src={iconUrl}
            className={styles[`${classNamePath}__downloadCodeView__shipdate--image`]}
          />
          <div className={styles[`${classNamePath}__downloadCodeView__shipdate--value`]}>{carrierName}</div>
        </div>
        <div className={styles[`${classNamePath}__downloadCodeView__shipdate`]}>
          <div className={styles[`${classNamePath}__downloadCodeView__shipdate--label`]}>{trackingLabel}:</div>
          <div className={styles[`${classNamePath}__downloadCodeView__shipdate--value`]}>{trackingNumber}</div>
        </div>

        <div className={styles[`${classNamePath}__downloadCodeView__qr`]}>
          <img
            src={labelUrl}
            alt="QR Code"
            className={styles[`${classNamePath}__downloadCodeView__qr--image`]}
          />
        </div>
      </div>
    </div>
  );
};

interface ShippingInfoProps {
  shipmentDetails?: ShipmentDetails;
  action?: TimelineShippingLabelAction;
}

const ShippingInfo = ({ shipmentDetails, action }: ShippingInfoProps) => {
  const router = useRouter();

  const { t } = useTranslation('orders-sales');

  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  const { selectedProduct: product, showToast, updatePopin } = use(TimelineContext);

  const { userType } = use(StepInfoContext);

  const { productId } = product || {};
  const { chosenCarrier, shippingLabelUrl, trackingNumber, labelUrl, dropOffLocationLink, shippingOption, labelType } =
    shipmentDetails || {};

  const { carrierName, iconUrl, carrierNote } = chosenCarrier || {};

  const [, copy] = useCopyToClipboard();

  const [showDropOff, setShowDropOff] = useState(false);

  const { selectedAddress: shippingAddress, refetch: refetchAddresses } = useGetShippingAddresses();

  const shippingActionBtnRef = useRef<HTMLButtonElement>(null);

  const { waitingToShipProduct } = getOrderSaleURLQueries();

  // This state is to make the Hook called every time  waitingToShipProduct triggered from Shipping Reminder
  // Otherwise the hook will memoir waitingToShipProduct and won't be called the second time
  const [bookmarkId, setBookmarkId] = useState<string | null>();

  const shippingType = useMemo(() => {
    switch (shippingOption) {
      case ShippingPickupTypeEnum.homeCollection:
        return t('MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.SHIPPING_OPTION.PICK_UP');
      case ShippingPickupTypeEnum.dropOff:
        return t('MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.SHIPPING_OPTION.DROP_OFF');
      default:
        return '';
    }
  }, [shippingOption, t]);

  const downloadCode = () => {
    sendAnalyticEvent('timeline_download_shipping_label', {
      label: userType,
      property: trackingNumber,
    });

    renderAndDownload(
      <DownloadView
        shipmentDetails={shipmentDetails}
        product={product}
        shipByLabel={t('MY_ACCOUNT.ORDERS_SALES.ACTION.GET_SHIPPING_LABEL.SHIP_BY_DATE')}
        trackingLabel={t('MY_ACCOUNT.ORDERS_SALES.ACTION.GET_SHIPPING_LABEL.TRACKING')}
        refLabel={t('MY_ACCOUNT.ORDERS_SALES.LIST.REF')}
      />,
      `${product.brandName}-${product.productName}-${product.productId}.jpeg`,
      labelUrl,
    );
  };

  const copyTrackingNumber = () => {
    sendAnalyticEvent('timeline_download_shipping_label_tracking_number', {
      label: userType,
      property: trackingNumber,
    });
    showToast({
      title: t('MY_ACCOUNT.ORDERS_SALES.ACTION.GET_SHIPPING_LABEL.COPY_TRACKING_NUMBER'),
      type: 'success',
    });
    copy(trackingNumber);
  };

  // This method triggered when click on Get Shipping Label from Shipping Reminder banner
  const handleGetShippingLabel = () => {
    // No label is generated to open the pop-up
    if (!shipmentDetails) {
      if (shippingActionBtnRef.current) {
        updatePopin({ kind: 'GetShippingLabel', product });
      }
      return;
    }
    // Printable label already generated, open the PDF in a new tab
    if (labelType === ShipmentLabelTypeEnum.printableLabel && shippingLabelUrl) {
      window.open(shippingLabelUrl, '_blank');
      return;
    }
    // QR code: auto download generated QR code
    if (labelType === ShipmentLabelTypeEnum.shippingCode && labelUrl) {
      if (shippingActionBtnRef.current) {
        shippingActionBtnRef.current.click();
      }
      return;
    }
  };

  // Automatically open Get Shipping Label popup
  // Triggered when URL params include "waitingToShipProduct" and the selected product has Get Shipping Label button
  useEffect(() => {
    if (!shippingAddress) {
      return;
    }

    setBookmarkId(waitingToShipProduct);

    if (waitingToShipProduct === productId) {
      handleGetShippingLabel();
    }
    // Remove the Product from URL
    if (waitingToShipProduct) {
      updateURLQueries(router.pathname, { waitingToShipProduct: null });
      setBookmarkId(null);
    }
    // eslint-disable-next-line
  }, [shippingActionBtnRef, productId, waitingToShipProduct, bookmarkId, shippingAddress]);

  // in case Non sponsor -> don't show address card
  const showAddressCard = action && ['getShippingLabel', 'editShippingDetails'].includes(action.type);

  if (!shipmentDetails) {
    return showAddressCard ? (
      <AddressCard
        refetch={refetchAddresses}
        address={shippingAddress}
        action={action}
        updateShipmentBtnRef={shippingActionBtnRef}
      />
    ) : null;
  }

  // TODO: EAA - Check keyboard flow for this action
  const onShipmentTracking = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    if (target && target.tagName === 'A') {
      const href = (target as HTMLAnchorElement).href;
      sendAnalyticEvent('timeline_hyperlink', {
        action: href,
        label: userType,
        property: 'shipping_info',
      });
    }
  };

  return (
    <div>
      <div className={styles[`${classNamePath}`]}>
        <div className={styles[`${classNamePath}__carrier`]}>
          <img
            alt={carrierName}
            src={iconUrl}
            className={styles[`${classNamePath}__carrier--image`]}
          />
          <div className={styles[`${classNamePath}__carrier--name`]}>{carrierName}</div>
          {!!shippingType && <div className={styles[`${classNamePath}__carrier--name`]}>{`- ${shippingType}`}</div>}
        </div>

        <div className={styles[`${classNamePath}__viewDropOff`]}>
          <div className={styles[`${classNamePath}__viewDropOff--inner`]}>
            <ShippingInstructionDescription
              shipmentDetails={shipmentDetails}
              carrierWebsite={dropOffLocationLink}
              onOpenDropOffModal={() => setShowDropOff(true)}
            />
          </div>
        </div>

        {trackingNumber && (
          <div className={styles[`${classNamePath}__viewTracking`]}>
            <div className={styles[`${classNamePath}__viewTracking--inner`]}>
              {t('MY_ACCOUNT.ORDERS_SALES.ACTION.GET_SHIPPING_LABEL.TRACKING')}:
              <span className={styles[`${classNamePath}__viewTracking--number`]}>{trackingNumber}</span>
              <Icon
                name="duplicate-default"
                onClick={copyTrackingNumber}
                className={styles[`${classNamePath}__viewTracking--icon`]}
              />
            </div>
          </div>
        )}

        {carrierNote && (
          <div
            className={styles[`${classNamePath}__carrierNote`]}
            dangerouslySetInnerHTML={{ __html: sanitizer(carrierNote) }}
            onClick={onShipmentTracking}
          />
        )}

        {labelUrl ? (
          <>
            <div className={styles[`${classNamePath}__qrCode`]}>
              <img
                src={labelUrl}
                alt="QR Code"
                className={styles[`${classNamePath}__qrCode--image`]}
              />
            </div>
            <button
              ref={shippingActionBtnRef}
              onClick={downloadCode}
              className={styles[`${classNamePath}__downloadCode`]}
            >
              {t(`${langPrefix}DOWNLOAD_CODE`)}
            </button>
          </>
        ) : (
          shippingLabelUrl && (
            <UrlActionView
              url={shippingLabelUrl}
              className={clsx(
                styles.ordersSales__mainRight__steps__step__box__actions__btn,
                'vc-btn vc-btn--full vc-btn--primary',
                styles[`${classNamePath}__downloadCodeButton`],
              )}
              newTab={true}
              onClick={() => sendAnalyticEvent('timeline_view_printable_label', { label: userType })}
            >
              {t(`${langPrefix}VIEW_SHIPPING_LABEL`)}
            </UrlActionView>
          )
        )}
      </div>

      {showAddressCard && (
        <AddressCard
          refetch={refetchAddresses}
          address={shippingAddress}
          updateShipmentBtnRef={shippingActionBtnRef}
        />
      )}

      {showDropOff && (
        <DropOff
          onClose={() => setShowDropOff(false)}
          address={shippingAddress}
          carrierName={chosenCarrier?.carrierName || ''}
          carrierLogo={chosenCarrier?.iconUrl}
          productId={product.productId}
        />
      )}
    </div>
  );
};

export default ShippingInfo;
